import React, { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OnboardingStep, StepStatus } from '@/contexts/OnboardingContext';
import appIcon from '@/assets/app-icon.png';

interface WizardShellProps {
  currentStep: OnboardingStep;
  stepStatuses: StepStatus[];
  onSkip?: () => void;
  children: ReactNode;
}

interface SidebarStep {
  index: number; // 1-5
  name: string;
  description: string;
}

const SIDEBAR_STEPS: SidebarStep[] = [
  { index: 1, name: 'Company Setup', description: 'Add your companies' },
  { index: 2, name: 'User Access', description: 'Set roles & company access' },
  { index: 3, name: 'Booking Types', description: 'Define your booking categories' },
  { index: 4, name: 'Planner Defaults', description: 'Configure planner settings' },
  { index: 5, name: 'Planner Groups', description: 'Organise your resources' },
];

function StepBadge({
  stepIndex,
  currentStep,
  status,
}: {
  stepIndex: number;
  currentStep: OnboardingStep;
  status: StepStatus | undefined;
}) {
  const isActive = currentStep === stepIndex;
  const isCompleted = status?.completed;
  const isSkipped = status?.skipped;

  if (isCompleted || isSkipped) {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: isCompleted ? '#1a8a8a' : '#d97706' }}
      >
        <Check className="h-4 w-4 text-white" strokeWidth={3} />
      </div>
    );
  }

  if (isActive) {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{
          backgroundColor: 'rgba(26, 138, 138, 0.15)',
          border: '2px solid #1a8a8a',
        }}
      >
        <span className="text-sm font-bold" style={{ color: '#1a8a8a' }}>
          {stepIndex}
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
      <span className="text-sm font-medium text-muted-foreground">{stepIndex}</span>
    </div>
  );
}

export function WizardShell({
  currentStep,
  stepStatuses,
  onSkip,
  children,
}: WizardShellProps) {
  return (
    /*
     * z-index strategy:
     * Wizard shell sits at z-[48] — below Radix UI's z-50 portals.
     * This ensures Sheet, Dialog, Select, Popover, AlertDialog overlays
     * (all at z-50) float correctly above the wizard when opened from
     * embedded settings pages.
     */
    <div
      className="fixed inset-0 bg-background flex"
      style={{ zIndex: 48 }}
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding Wizard"
    >
      {/* ── LEFT SIDEBAR ── */}
      <aside
        className="flex w-[280px] shrink-0 flex-col border-r"
        style={{ backgroundColor: 'rgba(26, 138, 138, 0.04)' }}
      >
        {/* App logo + name — h-16 matches the main top bar exactly */}
        <div className="flex h-16 shrink-0 items-center gap-3 border-b px-6">
          <img src={appIcon} alt="Scheduler" className="h-8 w-8 rounded-md object-cover" />
          <span className="text-base font-bold text-foreground">Scheduler</span>
        </div>

        {/* Section label */}
        <div className="px-6 pt-6 pb-3">
          <span
            className="text-[10px] font-semibold uppercase tracking-widest"
            style={{ color: '#1a8a8a' }}
          >
            Onboarding Setup
          </span>
        </div>

        {/* Step indicators */}
        <nav className="flex-1 overflow-y-auto px-4 pb-4">
          <ul className="space-y-1">
            {SIDEBAR_STEPS.map((step) => {
              const status = stepStatuses.find((s) => s.stepIndex === step.index);
              const isActive = currentStep === step.index;

              return (
                <li
                  key={step.index}
                  className={`flex items-start gap-3 rounded-lg px-3 py-3 transition-colors ${
                    isActive ? 'bg-primary/5' : ''
                  }`}
                >
                  <StepBadge
                    stepIndex={step.index}
                    currentStep={currentStep}
                    status={status}
                  />
                  <div className="min-w-0 pt-0.5">
                    <p
                      className={`text-sm font-semibold leading-tight ${
                        isActive ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {step.name}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
                      {step.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>


      </aside>

      {/* ── MAIN CONTENT AREA ── */}
      <div className="flex flex-1 flex-col min-h-0">
        {/* Top bar */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b bg-background px-8">
          <div className="flex items-center gap-3">
            <TopBarTitle currentStep={currentStep} />
          </div>
          {onSkip && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground hover:text-foreground"
              onClick={onSkip}
            >
              <X className="h-4 w-4" />
              Skip Onboarding
            </Button>
          )}
        </header>

        {/* Step content — fills remaining height */}
        <main className="flex-1 min-h-0 overflow-hidden">{children}</main>
      </div>
    </div>
  );
}

function TopBarTitle({ currentStep }: { currentStep: OnboardingStep }) {
  if (currentStep === 0) {
    return (
      <h1 className="text-lg font-semibold text-foreground">
        Welcome to Scheduler
      </h1>
    );
  }
  if (currentStep === 6) {
    return (
      <h1 className="text-lg font-semibold text-foreground">Setup Complete</h1>
    );
  }
  return (
    <h1 className="text-lg font-semibold text-foreground">
      Step {currentStep} of 5
    </h1>
  );
}
